/**
 * generateField
 */

import { ForwardedRef, forwardRef } from 'react';

// Lodash
import uniqueId from 'lodash/uniqueId';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { Controller, useFormContext } from 'react-hook-form';

import FormControlField from 'app/components/Form/FormControlField';

import { FieldComponentType } from 'app/components/Form/types/Field.types';

export const typeOfFields = {
  default: 'default',
  control: 'control',
  toggle: 'toggle',
  checkbox: 'checkbox',
};

/**
 * Where a component wants props for its underlying <input> element.
 *
 * MUI 9 removed the top-level `inputProps` escape hatch from some components
 * but not others, and the replacement slot name differs, so this cannot be a
 * single blanket rule:
 *
 *  - inputProps: InputBase descendants (OutlinedInput, Select) still accept it.
 *  - htmlInput:  TextField. Its `input` slot is the InputBase wrapper, so the
 *                raw element moved to `slotProps.htmlInput`.
 *  - input:      SwitchBase descendants (Switch, Checkbox) render the raw
 *                element in their `input` slot.
 *
 * Getting this wrong is silent - the props are simply dropped - so each entry
 * in Field.tsx is verified by the aria-label assertions in its own test.
 */
export const inputPropsTargets = {
  inputProps: 'inputProps',
  htmlInput: 'htmlInput',
  input: 'input',
} as const;

export type InputPropsTarget =
  (typeof inputPropsTargets)[keyof typeof inputPropsTargets];

const forwardToInput = (
  target: InputPropsTarget,
  attributes: Record<string, unknown>,
  callerProps: Record<string, any>,
) => {
  if (target === inputPropsTargets.inputProps) {
    return { inputProps: { ...attributes, ...callerProps.inputProps } };
  }

  // The caller's own slotProps is spread after ours, so merge here rather than
  // letting it replace the slot wholesale and silently drop the aria-label.
  const callerSlotProps = callerProps.slotProps ?? {};

  return {
    slotProps: {
      ...callerSlotProps,
      [target]: { ...attributes, ...callerSlotProps[target] },
    },
  };
};

export const generateField = (
  FormComponent,
  typeOfFormControl = typeOfFields.default,
  inputPropsTarget: InputPropsTarget = inputPropsTargets.inputProps,
) => {
  // To create an unique id with the parameter as prefix
  const fieldId = uniqueId('form-field-');

  const ModifiedFormComponent = forwardRef(
    (
      {
        rules,
        name,
        formControlProps,
        labelProps,
        className,
        ...props
      }: FieldComponentType,
      ref: ForwardedRef<unknown>,
    ) => {
      const {
        control,
        register,
        formState: { errors },
      } = useFormContext();

      const helperText = get(errors, `${name}.message`, '');

      const getComponent = () => {
        let errorProps: object = {
          error: !isEmpty(helperText),
        };

        // Toggle doesn't send error Property.
        if (
          [typeOfFields.toggle, typeOfFields.checkbox].includes(
            typeOfFormControl,
          )
        ) {
          errorProps = {};
        }

        if (
          [typeOfFields.control, typeOfFields.checkbox].includes(
            typeOfFormControl,
          )
        ) {
          return (
            <>
              <Controller
                render={({ field }) => (
                  <FormComponent
                    id={fieldId}
                    control={control}
                    {...field}
                    {...props}
                    {...forwardToInput(
                      inputPropsTarget,
                      { 'aria-label': name },
                      props,
                    )}
                    ref={ref}
                    {...errorProps}
                  />
                )}
                control={control}
                name={name}
                rules={rules}
                {...props}
              />

              <FormControlField.Error
                name={name}
                message={helperText.toString()}
              />
            </>
          );
        }

        return (
          <>
            <FormComponent
              className={className}
              id={fieldId}
              {...register(name, { ...rules })}
              {...props}
              {...forwardToInput(
                inputPropsTarget,
                { 'aria-label': name, autoComplete: 'off' },
                props,
              )}
              {...errorProps}
            />

            <FormControlField.Error
              name={name}
              message={helperText.toString()}
            />
          </>
        );
      };

      return (
        <>
          <FormControlField.FormControl {...formControlProps}>
            {labelProps && (
              <FormControlField.Label name={fieldId} {...labelProps} />
            )}

            {getComponent()}
          </FormControlField.FormControl>
        </>
      );
    },
  );

  ModifiedFormComponent.displayName = 'FieldComponent';

  return ModifiedFormComponent;
};

export default generateField;
